import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalFooter } from '../ui/modal';
import { Input, Textarea, Checkbox } from '../ui/form';
import { Button } from '../ui/button';
import { useCreateDivision, useUpdateDivision } from '../../hooks/use-divisions';
import { useDivisionContext } from '../../contexts/division-context';
import { useDivisionRoles } from '../../hooks/use-division-roles';
import { useWorkflowTemplates } from '../../hooks/use-workflow-templates';
import { useAuth } from '../../hooks/use-auth';
import { CreateDivisionRequest, UpdateDivisionRequest, Division, DefaultWorkflowStep } from '../../types';
import { Plus, Trash2 } from 'lucide-react';

interface DivisionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  division?: Division; // If provided, we're editing; otherwise creating
}

interface FormData {
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  includeInPublicRegistrationList: boolean;
  // Notification settings
  emailNotifications: boolean;
  slackNotifications: boolean;
  approvalReminders: boolean;
  reminderIntervalHours: number;
  // Escalation settings  
  autoEscalation: boolean;
  escalationDelayHours: number;
  maxEscalationLevels: number;
  // Timeout settings
  defaultStepTimeoutHours?: number;
  defaultPauseTimeoutHours?: number;
  defaultTimeoutWarningHours?: number;
  timeoutWarningNotificationEmail?: string;
  // Inactivity deactivation settings
  inactivityDeactivationDays?: number;
  inactivityWarningDaysInput?: string;
  // Approval review settings
  requireExplicitSubmit?: boolean;
  // Routing settings
  defaultRoutingRuleJsonPath?: string;
  // Default workflow steps
  defaultWorkflowSteps: DefaultWorkflowStep[];
  // Email extraction paths
  requesterEmailPaths: string[];
  requesterNamePaths: string[];
}

/** Parses a comma-separated "14, 7, 1" string into a deduped, descending list of positive day counts. */
function parseWarningDaysList(input?: string): number[] | undefined {
  if (!input || !input.trim()) return undefined;
  const days = Array.from(new Set(
    input.split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => parseInt(s, 10))
  )).sort((a, b) => b - a);
  return days.length > 0 ? days : undefined;
}

function validateWarningDaysList(input?: string): string | undefined {
  if (!input || !input.trim()) return undefined;
  const parts = input.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  for (const part of parts) {
    const n = Number(part);
    if (!Number.isInteger(n) || n < 1 || n > 3650) {
      return 'Must be a comma-separated list of whole numbers between 1 and 3650';
    }
  }
  return undefined;
}

export function DivisionFormModal({ isOpen, onClose, division }: DivisionFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState<DefaultWorkflowStep[]>([]);
  const [emailPaths, setEmailPaths] = useState<string[]>([]);
  const [namePaths, setNamePaths] = useState<string[]>([]);
  const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>([]);
  const [registrationWorkflowTemplateId, setRegistrationWorkflowTemplateId] = useState<string>('');
  const isEditing = !!division;

  const createDivisionMutation = useCreateDivision();
  const updateDivisionMutation = useUpdateDivision();
  const { user } = useAuth();
  const { setSelectedDivisionId, selectedDivisionId } = useDivisionContext();
  const { data: divisionRolesResponse } = useDivisionRoles(division?.id || selectedDivisionId || '', !!division?.id || !!selectedDivisionId);
  const { data: workflowTemplates } = useWorkflowTemplates(division?.id || '', undefined, isEditing);

  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors },
    watch
  } = useForm<FormData>();

  // Reset form values when division prop changes (for editing) or modal opens
  useEffect(() => {
    if (isOpen) {
      if (division) {
        reset({
          name: division.name,
          code: division.code || '',
          description: division.description || '',
          active: division.active,
          includeInPublicRegistrationList: division.includeInPublicRegistrationList ?? false,
          // Flatten notification settings
          emailNotifications: division.settings?.notificationSettings?.emailNotifications ?? true,
          slackNotifications: division.settings?.notificationSettings?.slackNotifications ?? false,
          approvalReminders: division.settings?.notificationSettings?.approvalReminders ?? true,
          reminderIntervalHours: division.settings?.notificationSettings?.reminderIntervalHours ?? 24,
          // Flatten escalation settings
          autoEscalation: division.settings?.escalationSettings?.autoEscalation ?? true,
          escalationDelayHours: division.settings?.escalationSettings?.escalationDelayHours ?? 48,
          maxEscalationLevels: division.settings?.escalationSettings?.maxEscalationLevels ?? 3,
          // Timeout settings
          defaultStepTimeoutHours: division.settings?.defaultStepTimeoutHours,
          defaultPauseTimeoutHours: division.settings?.defaultPauseTimeoutHours,
          defaultTimeoutWarningHours: division.settings?.defaultTimeoutWarningHours,
          timeoutWarningNotificationEmail: division.settings?.timeoutWarningNotificationEmail,
          // Inactivity deactivation settings
          inactivityDeactivationDays: division.settings?.inactivityDeactivationDays,
          inactivityWarningDaysInput: division.settings?.inactivityWarningDaysList?.join(', ') || '',
          // Approval review settings
          requireExplicitSubmit: division.settings?.requireExplicitSubmit ?? false,
          // Routing settings
          defaultRoutingRuleJsonPath: division.settings?.defaultRoutingRuleJsonPath || '',
          // Default workflow steps
          defaultWorkflowSteps: division.settings?.defaultWorkflowSteps || [],
          // Email extraction paths
          requesterEmailPaths: division.settings?.requesterEmailPaths || [],
          requesterNamePaths: division.settings?.requesterNamePaths || [],
        });
        setWorkflowSteps(division.settings?.defaultWorkflowSteps || []);
        setEmailPaths(division.settings?.requesterEmailPaths || []);
        setNamePaths(division.settings?.requesterNamePaths || []);
        setAllowedEmailDomains(division.settings?.allowedRegistrationEmailDomains || []);
        setRegistrationWorkflowTemplateId(division.settings?.registrationWorkflowTemplateId || '');
      } else {
        // Reset to empty form for create mode
        reset({
          name: '',
          code: '',
          description: '',
          active: true,
          includeInPublicRegistrationList: false,
          // Default notification settings
          emailNotifications: true,
          slackNotifications: false,
          approvalReminders: true,
          reminderIntervalHours: 24,
          // Default escalation settings
          autoEscalation: true,
          escalationDelayHours: 48,
          maxEscalationLevels: 3,
          // Default timeout settings
          defaultStepTimeoutHours: undefined,
          defaultPauseTimeoutHours: undefined,
          defaultTimeoutWarningHours: undefined,
          timeoutWarningNotificationEmail: undefined,
          // Default inactivity deactivation settings
          inactivityDeactivationDays: undefined,
          inactivityWarningDaysInput: '',
          // Default approval review settings
          requireExplicitSubmit: false,
          // Default routing settings
          defaultRoutingRuleJsonPath: '$.organizationIdentifier',
          // Default workflow steps
          defaultWorkflowSteps: [],
          // Email extraction paths
          requesterEmailPaths: [],
          requesterNamePaths: [],
        });
        setWorkflowSteps([]);
        setEmailPaths([]);
        setNamePaths([]);
        setAllowedEmailDomains([]);
        setRegistrationWorkflowTemplateId('');
      }
    }
  }, [division, reset, isOpen]);

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      console.log('Form data being submitted:', data);
      console.log('Is editing:', isEditing);
      
      // Construct the API request object from flattened form data
      const apiRequest: CreateDivisionRequest | UpdateDivisionRequest = {
        name: data.name,
        code: data.code || undefined,
        description: data.description || undefined,
        active: data.active,
        includeInPublicRegistrationList: data.includeInPublicRegistrationList,
        settings: {
          notificationSettings: {
            emailNotifications: data.emailNotifications,
            slackNotifications: data.slackNotifications,
            approvalReminders: data.approvalReminders,
            reminderIntervalHours: data.reminderIntervalHours,
          },
          escalationSettings: {
            autoEscalation: data.autoEscalation,
            escalationDelayHours: data.escalationDelayHours,
            maxEscalationLevels: data.maxEscalationLevels,
          },
          approvalThresholds: [],
          defaultStepTimeoutHours: data.defaultStepTimeoutHours || undefined,
          defaultPauseTimeoutHours: data.defaultPauseTimeoutHours || undefined,
          defaultTimeoutWarningHours: data.defaultTimeoutWarningHours || undefined,
          timeoutWarningNotificationEmail: data.timeoutWarningNotificationEmail || undefined,
          inactivityDeactivationDays: data.inactivityDeactivationDays || undefined,
          inactivityWarningDaysList: parseWarningDaysList(data.inactivityWarningDaysInput),
          requireExplicitSubmit: data.requireExplicitSubmit,
          defaultRoutingRuleJsonPath: data.defaultRoutingRuleJsonPath || undefined,
          defaultWorkflowSteps: workflowSteps,
          registrationWorkflowTemplateId: registrationWorkflowTemplateId || undefined,
          requesterEmailPaths: emailPaths.length > 0 ? emailPaths : undefined,
          requesterNamePaths: namePaths.length > 0 ? namePaths : undefined,
          allowedRegistrationEmailDomains: allowedEmailDomains.filter((d) => d.trim().length > 0).length > 0
            ? allowedEmailDomains.filter((d) => d.trim().length > 0)
            : undefined,
        }
      };

      // On create, pass along the admin's name from the id_token/userinfo profile so the
      // auto-provisioned DivisionAdmin user doesn't fall back to a placeholder name.
      if (!isEditing) {
        (apiRequest as CreateDivisionRequest).adminFirstName = user?.profile?.given_name;
        (apiRequest as CreateDivisionRequest).adminLastName = user?.profile?.family_name;
      }
      
      console.log('Final data being sent to API:', apiRequest);
      
      if (isEditing && division) {
        await updateDivisionMutation.mutateAsync({
          divisionId: division.id,
          request: apiRequest as UpdateDivisionRequest
        });
      } else {
        const newDivision = await createDivisionMutation.mutateAsync(apiRequest as CreateDivisionRequest);
        // Automatically switch to the newly created division
        setSelectedDivisionId(newDivision.id);
      }
      
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save division:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Division' : 'Create New Division'}
      size="2xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Division Name"
            {...register('name', { 
              required: 'Division name is required',
              minLength: { value: 1, message: 'Name must be at least 1 character' },
              maxLength: { value: 200, message: 'Name must be less than 200 characters' }
            })}
            error={errors.name?.message}
            placeholder="Enter division name"
          />

          <Input
            label="Division Code"
            {...register('code', {
              maxLength: { value: 10, message: 'Code must be less than 10 characters' }
            })}
            error={errors.code?.message}
            placeholder="Optional short code"
          />
        </div>

        <Textarea
          label="Description"
          rows={3}
          {...register('description', {
            maxLength: { value: 1000, message: 'Description must be less than 1000 characters' }
          })}
          error={errors.description?.message}
          placeholder="Enter division description"
        />

        <Checkbox
          label="Active Division"
          {...register('active')}
        />

        <Checkbox
          label="List on the public /register page"
          helperText="Also requires a registration link to be generated below (API Keys tab)."
          {...register('includeInPublicRegistrationList')}
        />

        {isEditing && division && (
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Division ID</label>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-secondary-500 dark:text-secondary-400 font-mono">{division.id}</span>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(division.id)}
                className="text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200"
                title="Copy division ID"
              >
                <i className="fa-regular fa-copy text-xs"></i>
              </button>
            </div>
          </div>
        )}

        {/* Notification Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Notification Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Email Notifications"
              {...register('emailNotifications')}
            />
            
            <Checkbox
              label="Slack Notifications"
              {...register('slackNotifications')}
            />
            
            <Checkbox
              label="Approval Reminders"
              {...register('approvalReminders')}
            />
            
            <Input
              type="number"
              label="Reminder Interval (hours)"
              {...register('reminderIntervalHours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 hour' },
                max: { value: 168, message: 'Must be less than 168 hours (1 week)' }
              })}
              error={errors.reminderIntervalHours?.message}
            />
          </div>
        </div>

        {/* Escalation Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Escalation Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Checkbox
              label="Auto Escalation"
              {...register('autoEscalation')}
            />
            
            <Input
              type="number"
              label="Escalation Delay (hours)"
              {...register('escalationDelayHours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 hour' },
                max: { value: 720, message: 'Must be less than 720 hours (30 days)' }
              })}
              error={errors.escalationDelayHours?.message}
            />
            
            <Input
              type="number"
              label="Max Escalation Levels"
              {...register('maxEscalationLevels', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 level' },
                max: { value: 10, message: 'Must be less than 10 levels' }
              })}
              error={errors.maxEscalationLevels?.message}
            />
          </div>
        </div>

        {/* Workflow Timeout Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Default Workflow Timeouts</h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            These default timeouts apply to all workflows in this division unless overridden at the individual workflow level.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Default Step Timeout (hours)"
              {...register('defaultStepTimeoutHours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 hour' },
                max: { value: 720, message: 'Must be less than 720 hours (30 days)' }
              })}
              error={errors.defaultStepTimeoutHours?.message}
              placeholder="Leave empty to use system default (48 hours)"
              helperText="Maximum time a workflow step can remain pending before timing out"
            />
            
            <Input
              type="number"
              label="Default Pause Timeout (hours)"
              {...register('defaultPauseTimeoutHours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 hour' },
                max: { value: 720, message: 'Must be less than 720 hours (30 days)' }
              })}
              error={errors.defaultPauseTimeoutHours?.message}
              placeholder="Leave empty to use system default (24 hours)"
              helperText="Default timeout for workflow pauses and escalations"
            />

            <Input
              type="number"
              label="Default Timeout Warning (hours before)"
              {...register('defaultTimeoutWarningHours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 hour' },
                max: { value: 720, message: 'Must be less than 720 hours (30 days)' }
              })}
              error={errors.defaultTimeoutWarningHours?.message}
              placeholder="Leave empty to disable warnings"
              helperText="Default hours before step timeout to send a warning notification"
            />

            <Input
              type="email"
              label="Timeout Warning Notification Email"
              {...register('timeoutWarningNotificationEmail', {
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Must be a valid email address' }
              })}
              error={errors.timeoutWarningNotificationEmail?.message}
              placeholder="Leave empty to skip division-level notification"
              helperText="Additional email to notify whenever a timeout warning fires for any order in this division"
            />
          </div>
        </div>

        {/* Approval Review Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Approval Review</h3>
          <Checkbox
            label='Require explicit "Submit" for all workflows in this division'
            helperText='Forces the draft/Submit Review flow (per-line Undo and Discard before committing) at every step of every workflow in this division, even if the workflow or step itself has it turned off.'
            {...register('requireExplicitSubmit')}
          />
        </div>

        {/* Inactivity Deactivation Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Account Inactivity Deactivation</h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            Automatically deactivate users in this division who haven't logged in for a configurable number of days.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Deactivate After (days)"
              {...register('inactivityDeactivationDays', {
                valueAsNumber: true,
                min: { value: 1, message: 'Must be at least 1 day' },
                max: { value: 3650, message: 'Must be less than 3650 days (10 years)' }
              })}
              error={errors.inactivityDeactivationDays?.message}
              placeholder="Leave empty to disable auto-deactivation"
              helperText="Days of inactivity (no login) after which a user is automatically deactivated"
            />

            <Input
              label="Warning Email (days before)"
              {...register('inactivityWarningDaysInput', {
                validate: (value) => validateWarningDaysList(value) ?? true
              })}
              error={errors.inactivityWarningDaysInput?.message}
              placeholder="e.g. 14, 7, 1"
              helperText="Comma-separated list of days before auto-deactivation to email the user a warning (one email per threshold)"
            />
          </div>
        </div>

        {/* Routing Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Routing Settings</h3>
          <div className="space-y-4">
            <Input
              label="Default Routing Rule JSON Path"
              {...register('defaultRoutingRuleJsonPath', {
                maxLength: { value: 500, message: 'JSON path must be less than 500 characters' }
              })}
              error={errors.defaultRoutingRuleJsonPath?.message}
              placeholder="$.organizationIdentifier"
              helperText="JSON path used to extract organization identifier from orders for automatic routing rule creation during user registration. Example: $.organizationIdentifier or $.header.shipTo.orgId"
            />
          </div>
        </div>

        {/* Email Extraction Settings */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Email Extraction Settings</h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            Configure JSONPath expressions to extract requester email and name from order documents. Paths are tried in order until a valid value is found.
          </p>

          {/* Email Paths */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Requester Email Paths</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEmailPaths([...emailPaths, ''])}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Email Path
              </Button>
            </div>
            {emailPaths.length === 0 ? (
              <div className="text-sm text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-md p-3">
                No email paths configured. Add JSONPath expressions to search for email addresses in order documents.
              </div>
            ) : (
              <div className="space-y-2">
                {emailPaths.map((path, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        value={path}
                        onChange={(e) => {
                          const updated = [...emailPaths];
                          updated[index] = e.target.value;
                          setEmailPaths(updated);
                        }}
                        placeholder="Order_Attributes[?(@.Name=='Email4')].Value"
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEmailPaths(emailPaths.filter((_, i) => i !== index));
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
              Examples: <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">Order_Attributes[?(@.Name=='Email4')].Value</code>, <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">RequesterEmail</code>, <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">$.customer.email</code>
            </p>
          </div>

          {/* Name Paths */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Requester Name Paths</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNamePaths([...namePaths, ''])}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Name Path
              </Button>
            </div>
            {namePaths.length === 0 ? (
              <div className="text-sm text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-md p-3">
                No name paths configured. Add JSONPath expressions to search for requester names in order documents.
              </div>
            ) : (
              <div className="space-y-2">
                {namePaths.map((path, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        value={path}
                        onChange={(e) => {
                          const updated = [...namePaths];
                          updated[index] = e.target.value;
                          setNamePaths(updated);
                        }}
                        placeholder="Order_Attributes[?(@.Name=='CustomerName')].Value"
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setNamePaths(namePaths.filter((_, i) => i !== index));
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
              Examples: <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">Order_Attributes[?(@.Name=='CustomerName')].Value</code>, <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">RequesterName</code>, <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">$.customer.name</code>
            </p>
          </div>
        </div>

        {/* Allowed Registration Email Domains */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white">Allowed Registration Email Domains</h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                Restrict self-registration to specific email domains. Leave empty to allow any domain.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAllowedEmailDomains([...allowedEmailDomains, ''])}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Domain
            </Button>
          </div>
          {allowedEmailDomains.length === 0 ? (
            <div className="text-sm text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-md p-3">
              No domain restrictions configured. Users may self-register with any email address.
            </div>
          ) : (
            <div className="space-y-2">
              {allowedEmailDomains.map((domain, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={domain}
                      onChange={(e) => {
                        const updated = [...allowedEmailDomains];
                        updated[index] = e.target.value;
                        setAllowedEmailDomains(updated);
                      }}
                      placeholder="army.mil"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAllowedEmailDomains(allowedEmailDomains.filter((_, i) => i !== index));
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
            Enter a specific domain (e.g. <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">army.mil</code>, allows only that domain and its subdomains)
            or a bare top-level/public suffix (e.g. <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">mil</code>, allows any subdomain ending in <code className="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 rounded">.mil</code>).
          </p>
        </div>

        {/* Registration Workflow Template */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Registration Workflow Template</h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            Choose a workflow template to use when the registration page auto-creates a workflow for a new organization identifier.
            When set, this takes precedence over the Default Workflow Steps configured below.
          </p>
          {isEditing ? (
            <select
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              value={registrationWorkflowTemplateId}
              onChange={(e) => setRegistrationWorkflowTemplateId(e.target.value)}
            >
              <option value="">None (use Default Workflow Steps below)</option>
              {workflowTemplates?.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-sm text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-md p-3">
              Save this division first, then edit it to select a registration workflow template.
            </div>
          )}
        </div>

        {/* Default Workflow Steps */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white">Default Workflow Steps</h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                Define default approval steps that will be created automatically when a workflow is generated during user registration
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newStep: DefaultWorkflowStep = {
                  step: workflowSteps.length + 1,
                  name: `Step ${workflowSteps.length + 1}`,
                  description: '',
                  approverType: 'user_specific',
                  divisionRoleId: undefined,
                  requiredApprovals: 1,
                  parallelApproval: false,
                };
                setWorkflowSteps([...workflowSteps, newStep]);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Step
            </Button>
          </div>

          {workflowSteps.length === 0 ? (
            <div className="text-center py-8 text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-md">
              No default workflow steps configured. Add steps to automatically create approval workflows during user registration.
            </div>
          ) : (
            <div className="space-y-4">
              {workflowSteps.map((step, index) => (
                <div key={index} className="border rounded-md p-4 bg-secondary-50 dark:bg-secondary-900">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium text-secondary-900 dark:text-white">Step {step.step}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = workflowSteps.filter((_, i) => i !== index);
                        // Renumber steps
                        updated.forEach((s, i) => s.step = i + 1);
                        setWorkflowSteps(updated);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Step Name"
                      value={step.name}
                      onChange={(e) => {
                        const updated = [...workflowSteps];
                        updated[index].name = e.target.value;
                        setWorkflowSteps(updated);
                      }}
                      placeholder="e.g., Manager Approval"
                    />

                    <div>
                      <label className="block text-sm font-medium mb-2">Approver Type</label>
                      <select
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        value={step.approverType}
                        onChange={(e) => {
                          const updated = [...workflowSteps];
                          updated[index].approverType = e.target.value;
                          setWorkflowSteps(updated);
                        }}
                      >
                        <option value="user_specific">User Specific (Registering User)</option>
                        <option value="division_role">Division Role</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {step.approverType === 'user_specific' ? 'Role Filter (Required)' : 'Division Role'}
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        value={step.divisionRoleId || ''}
                        onChange={(e) => {
                          const updated = [...workflowSteps];
                          updated[index].divisionRoleId = e.target.value;
                          setWorkflowSteps(updated);
                        }}
                      >
                        <option value="">Select a role...</option>
                        {divisionRolesResponse?.roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {step.approverType === 'user_specific' && (
                        <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                          Only users registering with this role will be assigned to this step
                        </p>
                      )}
                    </div>

                    <Input
                      label="Description (Optional)"
                      value={step.description || ''}
                      onChange={(e) => {
                        const updated = [...workflowSteps];
                        updated[index].description = e.target.value;
                        setWorkflowSteps(updated);
                      }}
                      placeholder="Describe this approval step"
                    />

                    <Input
                      type="number"
                      label="Required Approvals"
                      value={step.requiredApprovals}
                      onChange={(e) => {
                        const updated = [...workflowSteps];
                        updated[index].requiredApprovals = parseInt(e.target.value) || 1;
                        setWorkflowSteps(updated);
                      }}
                      min={1}
                    />

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`parallel-${index}`}
                        checked={step.parallelApproval}
                        onChange={(e) => {
                          const updated = [...workflowSteps];
                          updated[index].parallelApproval = e.target.checked;
                          setWorkflowSteps(updated);
                        }}
                        className="mr-2"
                      />
                      <label htmlFor={`parallel-${index}`} className="text-sm">
                        Parallel Approval
                      </label>
                    </div>

                    <Input
                      type="number"
                      label="Timeout Hours (Optional)"
                      value={step.timeoutHours || ''}
                      onChange={(e) => {
                        const updated = [...workflowSteps];
                        updated[index].timeoutHours = e.target.value ? parseInt(e.target.value) : undefined;
                        setWorkflowSteps(updated);
                      }}
                      placeholder="Leave empty for no timeout"
                    />

                    <Input
                      type="number"
                      label="Escalation Hours (Optional)"
                      value={step.escalationHours || ''}
                      onChange={(e) => {
                        const updated = [...workflowSteps];
                        updated[index].escalationHours = e.target.value ? parseInt(e.target.value) : undefined;
                        setWorkflowSteps(updated);
                      }}
                      placeholder="Leave empty for no escalation"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <ModalFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (isEditing ? 'Updating...' : 'Creating...') 
              : (isEditing ? 'Update Division' : 'Create Division')
            }
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
